import { useState } from 'react'

import {
  FlaggedType,
  MassActionRequest,
  ModerationItem,
  ModerationOpMode,
  ModerationStatus,
  PostStatus,
} from '@tribeplatform/gql-client/types'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useUpdateModerationItem } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { toast } from '@tribeplatform/react-ui-kit/Toast'
import { Tooltip } from '@tribeplatform/react-ui-kit/Tooltip'

import { PostRejectedModal } from './PostRejectedModal.js'

export const PendingPostButtons = ({
  moderationItem,
  setMassActonRequest,
}: {
  moderationItem: ModerationItem
  setMassActonRequest: (value: MassActionRequest) => void
}) => {
  const { $t } = useI18n()
  const [updating, setUpdating] = useState<
    'accepting' | 'rejecting' | 'hiding' | null
  >(null)
  const [rejectModal, setRejectModal] = useState<boolean>(null)
  const { mutateAsync: updateModerationItem } = useUpdateModerationItem()
  const pendingReview =
    moderationItem?.entity?.status === PostStatus.BLOCKED &&
    moderationItem?.flaggedBy === FlaggedType.SYSTEM

  const approveContent = async (moderationItemId: string) => {
    setUpdating('accepting')
    try {
      await updateModerationItem({
        id: moderationItemId,
        input: {
          status: ModerationStatus.REJECTED,
        },
      })
      toast({
        title: $t({
          defaultMessage: 'Post has been allowed',
          id: 'Moderation.Action.Allow.Success',
        }),
        status: 'success',
      })
    } catch (e) {
      toast({
        title: $t({
          defaultMessage: 'Unable to allow post',
          id: 'Moderation.Action.Allow.Fail',
        }),
        status: 'error',
      })
    } finally {
      setUpdating(null)
    }
  }

  const hideContent = async (moderationItem: ModerationItem) => {
    setUpdating('hiding')
    try {
      await updateModerationItem({
        id: moderationItem.id,
        input: {
          status: ModerationStatus.ACCEPTED,
          opMode: ModerationOpMode.Retain,
          sendNotification: false,
        },
      })
      toast({
        title: $t({
          defaultMessage: 'Post has been hidden',
          id: 'Moderation.Action.Allow.Hide',
        }),
        status: 'success',
      })
    } catch {
      toast({
        title: $t({
          defaultMessage: 'Unable to allow post',
          id: 'Moderation.Action.Allow.Fail',
        }),
        status: 'error',
      })
    } finally {
      setUpdating(null)
    }
  }

  return (
    <div className="flex space-s-3">
      <Button
        className="flex-1 justify-center"
        loading={updating === 'accepting'}
        disabled={!!updating}
        variant="primary"
        onClick={() => approveContent(moderationItem.id)}
      >
        {pendingReview ? (
          <T defaultMessage="Publish" id="Moderation.Post.Publish" />
        ) : (
          <T defaultMessage="Keep" id="Moderation.Post.Keep" />
        )}
      </Button>
      <Button
        className="flex-1 justify-center"
        loading={updating === 'hiding'}
        disabled={!!updating}
        variant="secondaryNeutral"
        onClick={() => hideContent(moderationItem)}
      >
        <div className="flex items-center gap-2 justify-center">
          <T
            defaultMessage="Publish as hidden"
            id="Moderation.Post.Buttons.Hide"
          />
          <Tooltip>
            <Tooltip.Trigger className="inline-block">
              <SvgIcon name="info" />
            </Tooltip.Trigger>
            <Tooltip.Panel>
              <T
                defaultMessage="The post will be published, hidden from members, and no notifications will be sent."
                id="Moderation.Post.Buttons.Hide.Tooltip"
              />
            </Tooltip.Panel>
          </Tooltip>
        </div>
      </Button>
      <Button
        className="flex-1 justify-center"
        loading={updating === 'rejecting'}
        disabled={!!updating}
        variant="secondaryNeutral"
        onClick={() => setRejectModal(true)}
      >
        <T defaultMessage="Remove" id="Moderation.Post.Delete" />
      </Button>
      {!!rejectModal && (
        <PostRejectedModal
          open={!!rejectModal}
          onClose={() => setRejectModal(null)}
          moderationItem={moderationItem}
          setMassActionRequest={setMassActonRequest}
        />
      )}
    </div>
  )
}
